import { FC, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';

import './StripeCheckoutSuccessPage.scss';
import {
  useAuthContext,
  useSubscriptionContext,
  useSyncSuccessStripePayment,
} from '../../../data-access';
import { ONBOARDING_PATH, Path } from '../../../routing';
import { getFirstOnboardingStepsAfterPaywall } from '../../../data-access';

const StripeCheckoutSuccessPage: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { refetchSubscriptions } = useSubscriptionContext();
  const { syncSuccessStripePayment, isPending: isSyncing } = useSyncSuccessStripePayment();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');
  const referralCampaignId = searchParams.get('referral_campaign_id');
  const rewardfulClientId = searchParams.get('rewardful_client_id');
  const discountParam = searchParams.get('discount');

  useEffect(() => {
    if (!sessionId || !user?.userId) {
      return;
    }

    const processSuccess = async () => {
      try {
        // Parse referral if available
        const referral =
          referralCampaignId && rewardfulClientId
            ? {
                campaignId: referralCampaignId,
                rewardfulClientId: rewardfulClientId,
              }
            : undefined;

        // Parse discount if available
        let discount: object | undefined;
        if (discountParam) {
          try {
            discount = JSON.parse(decodeURIComponent(discountParam));
          } catch (e) {
            console.warn('Failed to parse discount parameter:', e);
          }
        }

        // Sync the successful payment with the backend
        await syncSuccessStripePayment({
          userId: user.userId,
          sessionId,
          referral,
          discount,
        });

        // Refetch subscriptions to get the updated data
        await refetchSubscriptions();

        navigateToNextScreen();
      } catch (err) {
        console.error('Error in processSuccess:', err);
        setError("Couldn't process your subscription — reach out to support and we'll sort it out.");
        setIsProcessing(false);
      }
    };

    processSuccess();
  }, [
    sessionId,
    user?.userId,
    referralCampaignId,
    rewardfulClientId,
    discountParam,
    syncSuccessStripePayment,
    refetchSubscriptions,
    navigate,
  ]);

  const navigateToNextScreen = () => {
    setIsProcessing(false);
    const hasCompletedAfterPaywall = !!user?.onboarding?.afterPaywall;

    if (hasCompletedAfterPaywall) {
      navigate(Path.DASHBOARD, { replace: true });
    } else {
      const nextAfterPaywallStep = getFirstOnboardingStepsAfterPaywall();
      if (nextAfterPaywallStep) {
        navigate(ONBOARDING_PATH[nextAfterPaywallStep], { replace: true });
      } else {
        navigate(Path.DASHBOARD, { replace: true });
      }
    }
  };

  if (error) {
    return (
      <div className="stripe-checkout-success-page">
        <div className="stripe-checkout-success-page__error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate(Path.PAYWALL)}>Return to Subscribe</button>
        </div>
      </div>
    );
  }

  return (
    <div className="stripe-checkout-success-page">
      <div className="stripe-checkout-success-page__loading">
        <CircularProgress />
        <p>{isSyncing ? 'Syncing your subscription...' : 'Processing your subscription...'}</p>
      </div>
    </div>
  );
};

export default StripeCheckoutSuccessPage;
